import React from 'react';
import { PageHeader, Table } from 'react-bootstrap';
import OverlayTooltip from './OverlayTooltip'
import './Docs.css';

import SyntaxHighlighter from 'react-syntax-highlighter';
import { foundation } from 'react-syntax-highlighter/dist/styles';


//Info on sales, contact information (sales@splinter-log.com)
//link to discuss forum
//links to docs

class DocsCustomFormat extends React.Component {
	render() { 
		return (
			<div className="pageContents">
				<PageHeader>Custom Log Formats</PageHeader>
				Adding custom log definitions to Splinter is fairly straight-forward if you know your regex.

				<h3>Prerequisites</h3>
				<ul>
					<li>Good understanding of regular expressions.</li>
					<li>Sample logs to build your definition from.</li>
				</ul>

				<h3>Method</h3>
				<ol>
					<li>Create a text file with your favorite text editor.</li>
					<li>Build the definition as per the instructions here and place it in the text file.</li>
					<li>Open Splinter->Settings (Gear icon on the right).</li>
					<li>Browse and select the file you just created.</li>
					<li>Click OK.</li>
					<li>Restart Splinter.</li>
				</ol> 

				<h3>Building The Definitions File</h3>   
				<p>The best way to learn is by example, so let’s build the definition for the following custom logs:</p>
		
				<pre className="code">
				18 Apr 2016 23:46:02,863 [DEBUG] b5a0693d (main) Http11NioProtocol: Initializing ProtocolHandler<br/>
				18 Apr 2016 23:46:03,512 [INFO]  b5a0693d (main) Http11NioProtocol: Connection can be kept alive<br/>
				18 Apr 2016 23:46:03,671 [WARN]  b5a0693d (main) NioSelectorPool: Using a shared selector<br/>
				18 Apr 2016 23:46:03,702 [ERROR] b5a0693d (main) AprLifecycleListener: APR library not found!<br/>
				</pre>
				
				<p>In this particular example, we have 6 distinct components:</p>

				<ol>
					<li>The timestamp: <code>18 Apr 2016 23:46:02,863</code></li>
					<li>The log severity: <code>DEBUG</code></li>
					<li>A service request id associated with the log: <code>b5a0693d</code></li>
					<li>The thread name the code is running in: <code>main</code></li>
					<li>Class name: <code>Http11NioProtocol</code></li>
					<li>Log message: <code>Initializing ProtocolHandler</code></li>
				</ol>

				<h4>The Regex</h4>
				<p>Now let’s build a regex that matches the logs above. Follow these rules when writing your regex:</p>

				<ul>
					<li>Try to be fairly specific with the known fields to ensure that your regex won’t match against the wrong set of logs.</li>
					<li>Match each log component into a group. I.e. surround the timestamp in parenthesis.</li>
					<li>If the timestamp includes a millisecond value, group that value separately from the rest of the timestamp (you’ll see why later).</li>
					<li>Don’t forget to match against all log severity levels, even obscure ones if they exist (trace, fine, wtf)</li>
				</ul>
				<p>There’s a host of websites you can use to generate and test your <OverlayTooltip id="regex101.com" tooltip="regex101.com is pretty good.">regex</OverlayTooltip>. The following expression will match our logs:</p>

				<pre>
				{`^([0-9]{2} [A-Za-z]{3} [0-9]{4} [0-9:]{8}),([0-9]{1,3}) \\[([INFOWARDEBUGTL]{4,5})\\] +([A-Za-z0-9\\-]+)? \\(([^)]+)\\) ([0-9A-Za-z.$_]+): *(.*)`}
				</pre>
				<p>The expression will match the following groups:</p>
				
				<Table id="regexGroupTable" bordered striped hover>
					<thead>
						<tr>
							<th>Group</th>
							<th>Matched String</th>
							<th>Component</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>1</td>
							<td>18 Apr 2016 23:46:03</td>
							<td>Timestamp (no millis)</td>
						</tr>
						<tr>
							<td>2</td>
							<td>512</td>
							<td>millis</td>
						</tr>
						<tr>
							<td>3</td>
							<td>INFO</td>
							<td>Log Severity</td>
						</tr>
						<tr>
							<td>4</td>
							<td>b5a0693d</td>
							<td>Request ID</td>
						</tr>
						<tr>
							<td>5</td>
							<td>main</td>
							<td>Thead Name</td>
						</tr>
						<tr>
							<td>6</td>
							<td>Http11NioProtocol</td>
							<td>Class Name</td>
						</tr>
						<tr>
							<td>7</td>
							<td>Connection can be kept alive</td>
							<td>Log Message</td>
						</tr>
					</tbody>
				</Table>

				<h4>The Splinter Log Definition Format</h4>
				<p>A JSON format, which maps regex groups to columns. The general format is an array of configuration objects. Each configuration object must have the name, regex, message and order keys. All other configuration keys are optional.</p>

				<p>Make sure you double escape anything escaped in your regex. In our case, we escaped parenthesis {`\\(`}, brackets {`\\[`} and a dash {`\\-`}. These convert to \\(, \\[ and \\- respectively in JSON format.</p>

				<SyntaxHighlighter language='javascript' style={foundation}>{
`[
   {
    "name": "My Custom Log Format",
    "regex": "^([0-9]{2} [A-Za-z]{3} [0-9]{4} [0-9:]{8}),([0-9]{1,3}) \\\\[([INFOWARDEBUGTL]{4,5})\\\\] +([A-Za-z0-9\\\\-]+)? \\\\(([^)]+)\\\\) ([0-9A-Za-z.$_]+): *(.*)",
    "timestamp": {
      "group": 1,
      "format": "%d %b %Y %H:%M:%S",
      "ms_group": 2
    },
    "sev": {
      "group": 3,
      "format": [
        {
          "name": "DEBUG",
          "type": "verbose"
        },
        {
          "name": "INFO",
          "type": "normal"
        },
        {
          "name": "WARN",
          "type": "warning"
        },
        {
          "name": "ERROR",
          "type": "error"
        },
        {
          "name": "FATAL",
          "type": "error"
        }
      ]
    },
    "owner": {
      "group": 4,
      "name": "request"
    },
    "component": {
      "group": 5,
      "name": "thread",
      "visible": false
    },
    "subcomponent" : {
        "group": 6,
        "name": "class"
    },
    "message": {
      "group": 7
    },
    "order": [
      "timestamp",
      "sev",
      "owner",
      "component",
      "subcomponent",
      "message"
    ]
  }
]
`}</SyntaxHighlighter>
				<p>Now all you need to enable this format is save your configuration file and load it through the Splinter Settings.</p>
				
				<h3><small>Appendix: </small>Configuration Keys</h3>
				<p>The keys in <i>italics</i> are optional.</p>

				<Table bordered striped hover>
					<thead>
						<tr>
							<th>Configuration Key</th>
							<th>Description</th>
							<th>Parameters</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>name</td>
							<td>Freeform name of your log definition.</td>
							<td>none</td>
						</tr>
						<tr>
							<td>regex</td>
							<td>PCRE-compatible regex. You should start it with ^ for performance purposes.</td>
							<td>none</td>
						</tr>
						<tr>
							<td><i>timestamp</i></td>
							<td>Specifies which regex group should be used for displaying the timestamp. There is also a second group pointing to the milliseconds value. The format is specified using <a href="http://man7.org/linux/man-pages/man3/strptime.3.html" target="_blank">strptime</a> values. When relative is true and format is 'ms', the timestamp will be read as a relative number rather than fixed time.</td>
							<td>group, ms_group, format, relative, visible</td>
						</tr>
						<tr>
							<td><i>sev</i></td>
							<td>Specifies regex group matching the log severity. Then, specifies the meanings of each matched severity. There are only 4 recognized severity types <code>verbose</code>, <code>normal</code>, <code>warning</code>, and <code>error</code>.</td>
							<td>group, format, exclusive, visible</td>
						</tr>
						<tr>
							<td><i>owner, component, subcomponent</i></td>
							<td>Three arbitrary fields to store any <b>string</b> data from the log into. They specify the group they are built out of, as well as the name they should take in the UI. Set visible to false if you would like to hide the column by default (user can still show it by right-clicking on the table header).</td>
							<td>group, name, visible, graphNode, graphNodeClassTrim</td>
						</tr>
						<tr>
							<td><i>primary_id, secondary_id</i></td>
							<td>Two arbitrary fields to store any <b>integer</b> data from the log into. They specify the group they are built out of, as well as the name they should take in the UI. These two fields are sorted during the grouping process.</td>
							<td>group, name, visible</td>
						</tr>
						<tr>
							<td>message</td>
							<td>Specifies the log group that matches the actual meat of the log.</td>
							<td>group, name</td>
						</tr>
						<tr>
							<td>order</td>
							<td>Specifies the column display order in the UI log table.</td>
							<td>array of configuration keys</td>
						</tr>
					</tbody>
				</Table>
			</div>
			);
	}
}

export default DocsCustomFormat;





// WEBPACK FOOTER //
// ./src/DocsCustomFormat.js